import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { CellStyle, ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ConfigProvider, DatePicker } from 'antd';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IDataDefaulFilter } from '..';
import { getReportVoucherConversionByCompanyUnit, IReportVoucherConversionByCompanyUnit } from '../../../../api/reportCustomer/reportCustomerApi';
import useDebounce from '../../../../hook/useDebounce';
import useLoading from '../../../../hook/useLoading';
import useToast from '../../../../hook/useToast';
import { formatNumber } from '../../../../services/mixin';
import { selectorInfoUser } from '../../../../store/slices/infoUserSlice';
import '../styles.scss';

export interface IVoucherRedeemedNode {
    id: string;
    parentId?: string;
    name: string;
    months: Record<string, number>;
    total: number;
}

interface FlatRow extends IVoucherRedeemedNode {
    _level: number;
    _hasChildren: boolean;
}

const MONTH_KEYS = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];

const DEFAULT_MOCK_TREE: IVoucherRedeemedNode[] = [
    {
        id: '1',
        name: 'Cty TT/ĐL Phía Nam',
        months: { T1: 10000, T2: 10000, T3: 10000, T4: 10000, T5: 10000, T6: 10000, T7: 10000, T8: 10000, T9: 10000, T10: 10000, T11: 10000, T12: 10000 },
        total: 10000,
    },
    {
        id: '1-1',
        parentId: '1',
        name: 'THACO AUTO TP.HCM',
        months: { T1: 5000, T2: 5000, T3: 5000, T4: 5000, T5: 5000, T6: 5000, T7: 5000, T8: 5000, T9: 5000, T10: 5000, T11: 5000, T12: 5000 },
        total: 5000,
    },
    {
        id: '1-1-1',
        parentId: '1-1',
        name: 'SR Gò Vấp',
        months: { T1: 1000, T2: 1000, T3: 1000, T4: 1000, T5: 1000, T6: 1000, T7: 1000, T8: 1000, T9: 1000, T10: 1000, T11: 1000, T12: 1000 },
        total: 1000,
    },
    {
        id: '1-1-2',
        parentId: '1-1',
        name: 'SR Bình Triệu',
        months: { T1: 50, T2: 50, T3: 50, T4: 50, T5: 50, T6: 50, T7: 50, T8: 50, T9: 50, T10: 50, T11: 50, T12: 50 },
        total: 50,
    },
    {
        id: '1-1-3',
        parentId: '1-1',
        name: 'SR Bình Dương',
        months: { T1: 1000, T2: 1000, T3: 1000, T4: 1000, T5: 1000, T6: 1000, T7: 1000, T8: 1000, T9: 1000, T10: 1000, T11: 1000, T12: 1000 },
        total: 1000,
    },
    {
        id: '1-1-4',
        parentId: '1-1',
        name: 'SR Phú Nhuận',
        months: { T1: 50, T2: 50, T3: 50, T4: 50, T5: 50, T6: 50, T7: 50, T8: 50, T9: 50, T10: 50, T11: 50, T12: 50 },
        total: 50,
    },
    {
        id: '1-1-5',
        parentId: '1-1',
        name: 'SR Trường Chinh',
        months: { T1: 1000, T2: 1000, T3: 1000, T4: 1000, T5: 1000, T6: 1000, T7: 1000, T8: 1000, T9: 1000, T10: 1000, T11: 1000, T12: 1000 },
        total: 1000,
    },
    {
        id: '1-1-6',
        parentId: '1-1',
        name: 'SR Phú Mỹ Hưng',
        months: { T1: 50, T2: 50, T3: 50, T4: 50, T5: 50, T6: 50, T7: 50, T8: 50, T9: 50, T10: 50, T11: 50, T12: 50 },
        total: 50,
    },
    {
        id: '2',
        name: 'Cty TT/ĐL Phía Bắc',
        months: { T1: 20000, T2: 20000, T3: 20000, T4: 20000, T5: 20000, T6: 20000, T7: 20000, T8: 20000, T9: 20000, T10: 20000, T11: 20000, T12: 20000 },
        total: 20000,
    },
];

const mapVoucherRedeemedNodes = (
    nodes: IReportVoucherConversionByCompanyUnit[],
    parentId?: string,
): IVoucherRedeemedNode[] => nodes.flatMap((node) => {
    const id = String(node.id);
    return [
        {
            id,
            parentId,
            name: node.title,
            months: node.months,
            total: node.total,
        },
        ...mapVoucherRedeemedNodes(node.children ?? [], id),
    ];
});

function buildFlatRows(
    nodes: IVoucherRedeemedNode[],
    expanded: Set<string>,
): FlatRow[] {
    const childrenMap = new Map<string | undefined, IVoucherRedeemedNode[]>();
    for (const n of nodes) {
        const key = n.parentId;
        if (!childrenMap.has(key)) childrenMap.set(key, []);
        childrenMap.get(key)!.push(n);
    }

    const result: FlatRow[] = [];

    function walk(parentId: string | undefined, level: number) {
        for (const node of childrenMap.get(parentId) ?? []) {
            const hasChildren = childrenMap.has(node.id);
            result.push({ ...node, _level: level, _hasChildren: hasChildren });
            if (hasChildren && expanded.has(node.id)) {
                walk(node.id, level + 1);
            }
        }
    }

    walk(undefined, 0);
    return result;
}

interface Props {
    dataFilter: IDataDefaulFilter | undefined
}

const VoucherRedeemedByUnitMonthlyTable: React.FC<Props> = ({
    dataFilter,
}) => {
    const { unitTemp } = useSelector(selectorInfoUser)

    const pushToast = useToast();
    const pushLoading = useLoading()

    const [expanded, setExpanded] = useState<Set<string>>(new Set(['1', '1-1']));
    const [selectedYear, setSelectedYear] = useState<string | undefined>('2026');
    const [data, setData] = useState<IReportVoucherConversionByCompanyUnit[]>();

    const toggle = (id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };


    const gReportVoucherConversionByCompanyUnit = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportVoucherConversionByCompanyUnit(
            // pageSize, pageCurent,
            unitTemp, dataFilter?.unitIds, dataFilter?.campaignIds,
            // (YYYY-MM-DD)
            // dataFilter?.dateStart, dataFilter?.dateEnd,
            // Loại hình voucher (0 - Công khai, 1 - Theo nhóm, 2 - Theo cá nhân)
            dataFilter?.type,
            dataFilter?.typeVoucherId,
            Number(selectedYear)
        )
        if (result.status) {
            setData(result.data)
        }
        pushLoading(false)
    }, 500)

    const voucherRedeemedNodes = useMemo(
        () => mapVoucherRedeemedNodes(data ?? []),
        [data],
    );
    const rowData = useMemo(
        () => buildFlatRows(voucherRedeemedNodes, expanded),
        [voucherRedeemedNodes, expanded],
    );

    useEffect(() => {
        gReportVoucherConversionByCompanyUnit()
    }, [dataFilter?.campaignIds, dataFilter?.type, dataFilter?.typeVoucherId, dataFilter?.unitIds, selectedYear, unitTemp]);

    const columnDefs = useMemo<ColDef<FlatRow>[]>(() => {
        const cols: ColDef<FlatRow>[] = [
            {
                headerName: 'Công ty tỉnh thành',
                field: 'name',
                flex: 2,
                minWidth: 260,
                cellRenderer: (params: any) => {
                    const row: FlatRow = params.data;
                    const indent = row._level * 22;
                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: indent,
                                height: '100%',
                                cursor: row._hasChildren ? 'pointer' : 'default',
                            }}
                            onClick={() => {
                                if (row._hasChildren) {
                                    toggle(row.id);
                                }
                            }}
                        >
                            {row._hasChildren ? (
                                <span
                                    style={{
                                        marginRight: 6,
                                        color: '#595959',
                                        flexShrink: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {expanded.has(row.id) ? (
                                        <CaretDownOutlined style={{ fontSize: 12, color: '#595959' }} />
                                    ) : (
                                        <CaretRightOutlined style={{ fontSize: 12, color: '#595959' }} />
                                    )}
                                </span>
                            ) : (
                                <span style={{ marginRight: 6, width: 12, flexShrink: 0 }} />
                            )}
                            <span style={{ fontWeight: row._level === 0 ? 500 : 400, color: '#344054' }}>
                                {row.name}
                            </span>
                        </div>
                    );
                },
            },
        ];

        // 12 Tháng T1 -> T12
        MONTH_KEYS.forEach((monthKey) => {
            cols.push({
                headerName: monthKey,
                flex: 1,
                minWidth: 80,
                headerClass: 'ag-header-cell-center',
                valueGetter: (params) => params.data?.months?.[monthKey] ?? null,
                valueFormatter: (p) => (p.value == null ? '--' : String(formatNumber(p.value))),
                cellStyle: {
                    textAlign: 'center',
                    color: '#344054',
                } as CellStyle,
            });
        });

        // Cột Tổng
        cols.push({
            headerName: 'Tổng',
            field: 'total',
            flex: 1,
            minWidth: 90,
            headerClass: 'ag-header-cell-center',
            valueFormatter: (p) => (p.value == null ? '--' : String(formatNumber(p.value))),
            cellStyle: {
                textAlign: 'center',
                color: '#344054',
                fontWeight: 500,
            } as CellStyle,
        });

        return cols;
    }, [expanded]);




    return (
        <div className="pt-4 w-full">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 12 }}>
                <span className="text-base font-medium text-344054">
                    Báo cáo voucher được quy đổi tại công ty tỉnh thành theo tháng
                </span>
                <ConfigProvider locale={viVN}>
                    <DatePicker
                        picker="year"
                        placeholder="Năm 2026"
                        defaultValue={dayjs('2026')}
                        className="custom-input-date"
                        onChange={(date) => setSelectedYear(date?.format('YYYY') ?? undefined)}
                    />
                </ConfigProvider>
            </div>

            <div className="ag-theme-alpine custom-table-ag-v2" style={{ width: '100%' }}>
                <AgGridReact<FlatRow>
                    domLayout="autoHeight"
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={{ resizable: true, sortable: false }}
                    suppressMovableColumns
                    suppressCellFocus
                    rowHeight={44}
                    getRowId={(p) => p.data.id}
                    getRowStyle={(params) => ({
                        backgroundColor: params.rowIndex % 2 ? '#f7f9fc' : '#ffffff',
                    })}
                />
            </div>
        </div>
    );
};

export default VoucherRedeemedByUnitMonthlyTable;
